import React, { useMemo, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Bus, CalendarDays, MapPin, Users, ExternalLink, ShieldCheck, Mail } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/layout/Navbar';
import PageHeader from '../components/layout/PageHeader';
import { TOURS, RFU_TOURING_GUIDANCE_URL, TOURS_SUGGEST_EMAIL } from '../data/tours';
import {
  AGE_BANDS, canSeeTours, filterTours, monthOptions, monthLabel, dateLabel,
  ageGroupsLabel, needsTbcLabel, isUk, todayIso,
} from '../lib/tourListing';
import './ToursPage.css';

/**
 * /:clubSlug/tours — B-44.1 "Find a tour" (Tours & festivals).
 *
 * A Marakin-curated, read-only list of rugby union festivals, tournaments and
 * tour packages (data/tours.js — no API). Club admins, team managers and
 * coaches of rugby clubs only (lib/tourListing canSeeTours); everyone else is
 * sent back to the hub. Organiser links are plain target="_blank" anchors,
 * the app's pattern for external URLs — Capacitor hands them to the system
 * browser on native — and nothing here is built from window.location (F-010).
 *
 * WT-26: the subtitle and the safeguarding note agree. Marakin checks each
 * listing's details (the card's "Last checked" date); it doesn't vet the
 * organisers. The subtitle is that one sentence: PageHeader clamps at two
 * lines, and anything longer was cut off on a phone.
 */

const TYPE_LABEL = { festival: 'Festival', tournament: 'Tournament', 'tour package': 'Tour package' };
const REGIONS = [
  { key: '', label: 'Anywhere' },
  { key: 'uk', label: 'UK' },
  { key: 'overseas', label: 'Overseas' },
];

function TourCard({ tour }) {
  return (
    <article className="tours-card">
      <div className="tours-card-top">
        <span className="badge badge-green">{TYPE_LABEL[tour.type] || tour.type}</span>
        {!isUk(tour.country) && <span className="badge badge-silver">Overseas</span>}
      </div>
      <h3 className="tours-card-name">{tour.name}</h3>
      <p className="tours-card-organiser">{tour.organiser}</p>

      <dl className="tours-card-facts">
        <div>
          <dt><CalendarDays size={15} aria-hidden="true" /><span className="tours-sr">Dates</span></dt>
          <dd>
            {tour.dates}
            {needsTbcLabel(tour) && <span className="badge badge-gold tours-tbc">Dates TBC</span>}
          </dd>
        </div>
        <div>
          <dt><MapPin size={15} aria-hidden="true" /><span className="tours-sr">Place</span></dt>
          <dd>{tour.place}, {tour.country}</dd>
        </div>
        <div>
          <dt><Users size={15} aria-hidden="true" /><span className="tours-sr">Age groups</span></dt>
          <dd>{ageGroupsLabel(tour.ageGroups)}</dd>
        </div>
      </dl>
      <p className="tours-card-format">{tour.format}</p>

      <div className="tours-card-foot">
        <a
          href={tour.url}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-secondary btn-sm tours-visit"
        >
          Visit organiser <ExternalLink size={14} aria-hidden="true" />
        </a>
        <span className="tours-checked">Last checked {dateLabel(tour.lastChecked)}</span>
      </div>
    </article>
  );
}

export default function ToursPage() {
  const { clubSlug } = useParams();
  const { user, loading, getMembership } = useAuth();
  const membership = clubSlug ? getMembership(clubSlug) : null;

  const { data: club, isLoading: clubLoading } = useQuery({
    queryKey: ['club', clubSlug],
    queryFn: () => axios.get(`/clubs/${clubSlug}`).then(r => r.data),
    enabled: !!clubSlug,
  });

  const [band, setBand] = useState('');
  const [region, setRegion] = useState('');
  const [month, setMonth] = useState('');

  const today = todayIso();
  const months = useMemo(() => monthOptions(TOURS, today), [today]);
  const { tours, listedCount, undatedHidden } = useMemo(
    () => filterTours(TOURS, { band, region, month }, today),
    [band, region, month, today]
  );
  const filtered = !!(band || region || month);
  const clearFilters = () => { setBand(''); setRegion(''); setMonth(''); };

  if (!loading && !user) return <Navigate to="/login" replace />;
  if (loading || clubLoading) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--surface)' }}>
        {club ? <Navbar club={club} /> : <Navbar />}
        <div className="page-loading"><div className="spinner" /></div>
      </div>
    );
  }
  if (!canSeeTours(club?.sport || membership?.sport, membership)) {
    return <Navigate to={`/${clubSlug}`} replace />;
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)' }}>
      <Navbar club={club} />
      <PageHeader
        icon={Bus}
        title="Tours & festivals"
        subtitle="Each listing shows when Marakin last checked it."
      />

      <div className="tours-page">
        <section className="tours-filters" aria-label="Filter tours">
          <div className="tours-filter">
            <span className="label" id="tours-band-label">Age group</span>
            <div className="pill-group tours-pills" role="group" aria-labelledby="tours-band-label">
              <button type="button" className={`pill-btn ${band === '' ? 'active' : ''}`} onClick={() => setBand('')}>All</button>
              {AGE_BANDS.map(b => (
                <button
                  key={b.key}
                  type="button"
                  className={`pill-btn ${band === b.key ? 'active' : ''}`}
                  onClick={() => setBand(b.key)}
                  title={b.range}
                >
                  {b.label} <span className="tours-pill-range">{b.range}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="tours-filter-row">
            <div className="tours-filter">
              <span className="label" id="tours-region-label">Where</span>
              <div className="pill-group tours-pills" role="group" aria-labelledby="tours-region-label">
                {REGIONS.map(r => (
                  <button
                    key={r.key || 'any'}
                    type="button"
                    className={`pill-btn ${region === r.key ? 'active' : ''}`}
                    onClick={() => setRegion(r.key)}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="tours-filter tours-filter-month">
              <label className="label" htmlFor="tours-month">Month</label>
              <select id="tours-month" className="input" value={month} onChange={e => setMonth(e.target.value)}>
                <option value="">Any month</option>
                {months.map(m => <option key={m} value={m}>{monthLabel(m)}</option>)}
              </select>
            </div>
          </div>
        </section>

        <div className="tours-count" aria-live="polite">
          <span>
            {filtered
              ? `Showing ${tours.length} of ${listedCount}`
              : `${listedCount} ${listedCount === 1 ? 'listing' : 'listings'}`}
          </span>
          {filtered && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={clearFilters}>Clear filters</button>
          )}
        </div>
        {month && undatedHidden > 0 && (
          <p className="tours-note">
            {undatedHidden === 1 ? '1 tour with flexible dates isn’t' : `${undatedHidden} tours with flexible dates aren’t`} tied
            to a month, so {undatedHidden === 1 ? 'it’s' : 'they’re'} hidden. Choose “Any month” to see {undatedHidden === 1 ? 'it' : 'them'}.
          </p>
        )}

        {tours.length > 0 ? (
          <div className="tours-grid">
            {tours.map(t => <TourCard key={t.id} tour={t} />)}
          </div>
        ) : (
          <div className="tours-empty">
            <p>Nothing matches those filters yet. Club festivals often publish dates between now and February.</p>
            {filtered && <button type="button" className="btn btn-secondary btn-sm" onClick={clearFilters}>Clear filters</button>}
          </div>
        )}

        <aside className="tours-safeguarding">
          <ShieldCheck size={20} aria-hidden="true" className="tours-safeguarding-icon" />
          <div>
            <h2>Touring with age-grade players</h2>
            <p>
              Age-grade teams need permission to tour (RFU Regulation 15), and any tour or overnight stay should
              follow the RFU&rsquo;s Touring with Children guidance. Overseas tours also need the host union&rsquo;s
              permission. Marakin lists these events for information only. It checks each listing&rsquo;s details on
              the organiser&rsquo;s own page but hasn&rsquo;t vetted the organisers, so check their safeguarding
              arrangements before you book.
            </p>
            <a href={RFU_TOURING_GUIDANCE_URL} target="_blank" rel="noopener noreferrer" className="tours-link">
              RFU guidance on touring <ExternalLink size={13} aria-hidden="true" />
            </a>
          </div>
        </aside>

        <p className="tours-suggest">
          <Mail size={15} aria-hidden="true" />
          <span>
            Know a festival we&rsquo;re missing? Email{' '}
            <a href={`mailto:${TOURS_SUGGEST_EMAIL}`} className="tours-link">{TOURS_SUGGEST_EMAIL}</a>
          </span>
        </p>
      </div>
    </div>
  );
}
